import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  TrendingUp,
  Calendar,
  DollarSign,
  Filter
} from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { MemberApi } from '../services';

const currencyFormatter = new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
};

type Contribution = {
  id: number;
  amount: number;
  month: string;
  status: string;
  date_paid?: string | null;
  payment_method?: string | null;
  transaction_id?: string | null;
  created_at: string;
};

export const ContributionHistoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [statistics, setStatistics] = useState({
    total_contributed: 0,
    paid_count: 0,
    unpaid_count: 0,
    total_months: 0,
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await MemberApi.getContributionHistory();
      setContributions(data.contributions);
      setStatistics(data.statistics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contribution history');
    } finally {
      setLoading(false);
    }
  };

  const filteredContributions = contributions.filter((contrib) => {
    if (statusFilter === 'all') return true;
    return contrib.status === statusFilter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
      case 'unpaid':
        return <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />;
      case 'overdue':
      case 'penalized':
        return <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-slate-600 dark:text-slate-400" />;
      default:
        return <Clock className="h-5 w-5 text-slate-600 dark:text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider';
    
    switch (status) {
      case 'paid':
        return (
          <span className={`${baseClasses} border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-300`}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Paid
          </span>
        );
      case 'unpaid':
        return (
          <span className={`${baseClasses} border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-300`}>
            <Clock className="h-3.5 w-3.5" />
            Unpaid
          </span>
        );
      case 'overdue':
      case 'penalized':
        return (
          <span className={`${baseClasses} border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-300`}>
            <XCircle className="h-3.5 w-3.5" />
            {status === 'penalized' ? 'Penalized' : 'Overdue'}
          </span>
        );
      case 'failed':
        return (
          <span className={`${baseClasses} border border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400`}>
            <XCircle className="h-3.5 w-3.5" />
            Failed
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300`}>
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <AnimatedSection>
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-900/50 dark:bg-rose-900/10">
              <p className="text-rose-700 dark:text-rose-300">{error}</p>
              <button
                onClick={fetchHistory}
                className="mt-4 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                Try Again
              </button>
            </div>
          </AnimatedSection>
        ) : (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <AnimatedSection>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-brand-100 p-3 dark:bg-brand-900/30">
                      <TrendingUp className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Total Contributed</p>
                      <p className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                        {formatCurrency(statistics.total_contributed)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-emerald-100 p-3 dark:bg-emerald-900/30">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Paid</p>
                      <p className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                        {statistics.paid_count}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900/30">
                      <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Unpaid</p>
                      <p className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                        {statistics.unpaid_count}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-violet-100 p-3 dark:bg-violet-900/30">
                      <Calendar className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Total Months</p>
                      <p className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                        {statistics.total_months}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Filter */}
            <AnimatedSection delay={0.1}>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <Filter className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <span className="text-sm font-semibold text-slate-900 dark:text-white">Filter:</span>
                <div className="flex flex-wrap gap-2">
                  {['all', 'paid', 'unpaid', 'overdue', 'penalized', 'failed'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold capitalize transition ${
                        statusFilter === filter
                          ? 'bg-brand-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Contributions List */}
            <AnimatedSection delay={0.2}>
              {filteredContributions.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
                  <DollarSign className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-600" />
                  <p className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                    No contributions found
                  </p>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {statusFilter !== 'all'
                      ? 'Try changing the filter to see more contributions.'
                      : 'Your contribution history will appear here.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredContributions.map((contribution, index) => (
                    <motion.div
                      key={contribution.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="rounded-2xl border border-slate-200 bg-white p-6 transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          {getStatusIcon(contribution.status)}
                          <div>
                            <p className="font-display text-lg font-bold text-slate-900 dark:text-white">
                              {contribution.month}
                            </p>
                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                              {contribution.date_paid
                                ? `Paid on ${formatDate(contribution.date_paid)}`
                                : `Due date passed`}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-display text-xl font-bold text-slate-900 dark:text-white">
                              {formatCurrency(contribution.amount)}
                            </p>
                            {contribution.payment_method && (
                              <p className="mt-1 text-xs uppercase text-slate-600 dark:text-slate-400">
                                via {contribution.payment_method}
                              </p>
                            )}
                          </div>
                          {getStatusBadge(contribution.status)}
                        </div>
                      </div>

                      {contribution.transaction_id && (
                        <div className="mt-4 rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            Transaction ID: <span className="font-mono">{contribution.transaction_id}</span>
                          </p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatedSection>
          </div>
        )}
      </div>
    </div>
  );
};

